<script lang="ts">
import { useTerms } from '~/api/drupal'
import { useScrollToTargetOnChange } from '~/composables/useScrollToTargetOnChange'

export const useInstitutions = async () => {
  const route = useRoute()
  const { terms } = await useTerms(['institution_sector'])

  return {
    institutions: terms.value.institution_sector,
    active: route.query.institution ? (route.query.institution as string) : '93',
    options: terms.value.institution_sector.map(({ id, name }) => ({
      label: name,
      value: id,
    })),
  }
}

export const useSectors = async (institutions: string[] = ['0']) => {
  const route = useRoute()
  const { terms } = await useTerms(['institution_sector'], institutions)

  const sectors = terms.value.institution_sector.reduce(
    (acc, term: Term) => {
      acc[term.parent] = [term, ...(acc[term.parent] || [])]
      return acc
    },
    {} as Record<string, Term[]>,
  )

  for (const sector in sectors) {
    // sectors[sector].sort((a: Term, b: Term) => b.name.localeCompare(a.name))
    sectors[sector].reverse()
  }
  return {
    sectors,
    active: route.query.sector ? [route.query.sector as string] : [],
  }
}
</script>
<script setup lang="ts">
import { Term, useEmployees } from '~/api/drupal'

useHead({
  bodyAttrs: {
    class: 'employees-listing',
  },
})

const { t } = useI18n()

const { institutions, active: activeInstitution, options: institutionOptions } = await useInstitutions()
const { sectors, active: activeSector } = await useSectors(institutions.map(({ id }) => id))

const filter = reactive({
  institution: ref(activeInstitution),
  sector: ref(activeSector),
})

watch(
  () => ({ ...filter }),
  (current, previous) => {
    if (current.institution !== previous.institution) filter.sector = []
  },
)

const { items, page, pages } = await useEmployees({ filter })
const { scrollTarget } = useScrollToTargetOnChange(page)
</script>

<template>
  <SectionFilters>
    <div v-animate="{ animation: 'vertical-reveal' }">
      <Select
        v-model="filter.institution"
        name="institution"
        :required="true"
        :label="t('employee.list.filters.institution')"
        :options="institutionOptions"
      />
    </div>
    <TransitionGroup name="slide-fade" mode="out-in" tag="ul" class="sectors cul">
      <li v-for="(value, key) in sectors" v-show="filter.institution === key" :key="key" class="sector">
        <Tags v-model:active="filter.sector" :tags="value" />
      </li>
    </TransitionGroup>
  </SectionFilters>
  <Section>
    <div ref="scrollTarget" class="tl-11 tl-o-1">
      <p v-if="items?.length === 0" v-animate="{ animation: 'vertical-reveal' }">
        {{ t('employee.list.noResults') }}
      </p>
      <EmployeeList v-if="items.length" :employees="items" />
      <Pagination v-if="pages > 1" v-model:page="page" :max="pages" />
    </div>
  </Section>
</template>
<style scoped lang="scss">
.section-filters {
  :deep(.custom-select-input) {
    --form-input-border-color: var(--c-primary) !important;
    --form-input-label-color: var(--c-primary) !important;
    --form-input-color: var(--c-primary) !important;
    --form-input-background: var(--c-primary) !important;

    @include breakpoint(t) {
      width: 400px;
    }
  }
}

.sectors {
  margin-bottom: 0;
}
.sector {
  @include breakpoint(tl down) {
    position: relative;
    width: calc(50% + 50vw);
    overflow-x: auto;
    overflow-y: hidden;
  }
}
.tags {
  @include breakpoint(tl down) {
    flex-wrap: nowrap;
  }

  :deep(li) {
    flex-shrink: 0;
  }
}
.pagination {
  margin-top: 48px;
}
</style>
